import React, { useState } from 'react';
import { Modal, Select, message } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { ButtonWrapper } from 'components/ButtonWrapper';
import { addUser } from 'store/pageSlice';
import { getClientList } from 'store/clientSlice';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		box-sizing: border-box;
		width: 628px !important;
		background: #fff;
		border-radius: 8px;
		padding: 26px 32px 28px 32px;
		position: relative;
		left: 0;
		top: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 24px;
	}
	.ant-modal-header {
		border-bottom: none;
		padding: 0;
		background: #fff;
		display: none;
	}
	.ant-modal-footer {
		display: none;
	}
`;

const ModalSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 48px;
	width: 564px;
	flex: 1 1 auto;
`;

const ModalFooter = styled.div`
	display: flex;
	flex-direction: row;
	gap: 27px;
	width: 100%;
	margin-top: 32px;
	justify-content: center;
`;

const Title = styled.div`
	font-family: 'Inter', sans-serif;
	font-weight: 600;
	font-size: 24px;
	color: #000;
	line-height: 29px;
	margin-bottom: 24px;
`;

const FieldLabel = styled.div`
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 16px;
	color: #0c0c0c;
	margin-bottom: 8px;
`;

const FieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 0;
	gap: 8px;
	width: 566px;
`;

const StyledSelect = styled(Select)`
	width: 566px !important;
	height: 40px !important;
	background: #fff !important;
	border: 1px solid #c6c6c8 !important;
	border-radius: 2px !important;
	.ant-select-selector {
		background: #fff !important;
		border: none !important;
		height: 40px !important;
		font-size: 12px;
		color: #000;
		align-items: center;
		display: flex;
	}
`;

const BlockedRow = styled.div`
	box-sizing: border-box;
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 10px 12px;
	width: 566px;
	height: 40px;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	background: #f5f5f5;
	font-family: 'Inter', sans-serif;
	font-size: 12px;
	color: #000;
`;

export default function ChangeCityManagerModal({
	open,
	onCancel,
	currentManager,
	buId,
	refreshTeam,
}) {
	const [selectedKey, setSelectedKey] = useState(undefined);
	const [loading, setLoading] = useState(false);
	const [userOptions, setUserOptions] = useState([]);
	const dispatch = useDispatch();

	React.useEffect(() => {
		if (open) {
			setLoading(true);
			dispatch(
				getClientList({
					search_type: 'employee',
					filter_by: { user_type: 'city_manager' },
					limit: 100,
				})
			)
				.then((res) => {
					const options = (res?.rows || res?.data || []).map((item) => ({
						value: item.id,
						label:
							`${item.first_name || ''} ${item.last_name || ''}`.trim() ||
							item.username ||
							'',
					}));
					setUserOptions(options);
				})
				.finally(() => setLoading(false));
		}
	}, [open, dispatch]);

	return (
		<StyledModal
			open={open}
			onCancel={onCancel}
			footer={null}
			centered
			width={628}
			bodyStyle={{ padding: 0 }}
		>
			<Title>Change City Manager</Title>

			<ModalSection>
				<FieldGroup>
					<FieldLabel>Current City Manager</FieldLabel>
					<BlockedRow>{currentManager ? currentManager.name : '-'}</BlockedRow>
				</FieldGroup>

				<FieldGroup>
					<FieldLabel>New City Manager *</FieldLabel>
					<StyledSelect
						placeholder="Select Member"
						value={selectedKey}
						onChange={setSelectedKey}
						options={userOptions}
						loading={loading}
						showSearch
						filterOption={(input, option) =>
							option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
					/>
				</FieldGroup>
			</ModalSection>

			<ModalFooter>
				<ButtonWrapper
					onClick={onCancel}
					style={{
						border: '1px solid #FF3B30',
						color: '#FF3B30',
						background: '#fff',
						borderRadius: 4,
						height: 40,
						minWidth: 135.5,
						fontSize: 16,
						fontWeight: 500,
					}}
				>
					Cancel
				</ButtonWrapper>
				<ButtonWrapper
					type="primary"
					style={{
						background: '#143E60',
						color: '#fff',
						border: 'none',
						borderRadius: 4,
						height: 40,
						minWidth: 135.5,
						fontSize: 16,
						fontWeight: 500,
					}}
					disabled={!selectedKey || loading}
					loading={loading}
					onClick={async () => {
						const newManager = userOptions.find((opt) => opt.value === selectedKey);
						if (!newManager) return;
						setLoading(true);
						try {
							const today = new Date();
							const yyyy = today.getFullYear();
							const mm = String(today.getMonth() + 1).padStart(2, '0');
							const dd = String(today.getDate()).padStart(2, '0');
							const appointedOn = `${yyyy}-${mm}-${dd}`;
							await dispatch(
								addUser({
									data: {
										update_type: 'replace_city_manager',
										data: {
											bu_id: buId,
											new_city_manager_id: selectedKey,
											appointed_on: appointedOn,
										},
									},
									isEditMode: true,
								})
							);
							message.success('City Manager changed successfully');
							setSelectedKey(undefined);
							window.location.reload();
							onCancel();
							if (refreshTeam) refreshTeam();
						} catch (e) {
							message.error('Failed to change City Manager');
						} finally {
							setLoading(false);
						}
					}}
				>
					Change
				</ButtonWrapper>
			</ModalFooter>
		</StyledModal>
	);
}

ChangeCityManagerModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	currentManager: PropTypes.object,
	buId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	refreshTeam: PropTypes.func,
};
